// Chakra imports
import { 
  Box, 
  Flex, 
  Grid, 
  Icon, 
  Text, 
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Button,
  Badge,
  Progress
} from "@chakra-ui/react";
import React, { useState, useEffect, useCallback } from "react";
import { FaWallet, FaExclamationTriangle, FaCheckCircle, FaHourglassHalf } from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from "@chakra-ui/react";

// Servicios
import {
  getPaymentStats,
  getPaymentsWithFilters,
  getOverduePayments,
  getSavedBillingConfig,
  saveBillingConfig,
  formatCurrency,
  syncCustomerToOrders,
  syncOrdersByIds,
  triggerStatusChangeBatch,
  bulkUpdateInvoiceNumbers
} from "services/billing";
import { exportPaymentData } from "services/exportService";
import { getCustomers, getCredentials, getConfig } from "services/woo";

// Componentes
import PaymentStatistics from "./components/PaymentStatistics";
import PaymentFilters from "./components/PaymentFilters";
import PaymentTable from "./components/PaymentTable";
import OverdueReportsButton from "./components/OverdueReportsButton";
import NapsisImporter from "../Suscriptions/components/PaymentSync/NapsisImporter";
import SyncComparator from "../Suscriptions/components/PaymentSync/SyncComparator";
import SyncResults from "../Suscriptions/components/PaymentSync/SyncResults";
import paymentSyncService from "../../../services/paymentSync";

function Billing() {
  // Estados
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [payments, setPayments] = useState({ data: [], total: 0, total_pages: 1 });
  const [overduePayments, setOverduePayments] = useState({ data: [], total: 0 });
  const savedConfig = getSavedBillingConfig();
  const [filters, setFilters] = useState(savedConfig.filters);
  const [pagination, setPagination] = useState(savedConfig.pagination);
  const [isExporting, setIsExporting] = useState(false);
  const [napsisModalOpen, setNapsisModalOpen] = useState(false);
  
  // Estados para NAPSIS sync
  const [napsisStep, setNapsisStep] = useState(0);
  const [napsisData, setNapsisData] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [isExecutingSync, setIsExecutingSync] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  // Estados para la selección de acciones en el paso "Revisar"
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [selectAllUpdates, setSelectAllUpdates] = useState(true);
  const [selectAllCreations, setSelectAllCreations] = useState(true);
  
  // Estados para procesamiento masivo con progreso
  const [massiveProgress, setMassiveProgress] = useState({
    isActive: false,
    current: 0,
    total: 0,
    percentage: 0,
    currentChunk: 0,
    totalChunks: 0,
    errors: [],
    completed: 0,
    status: 'idle', // idle, processing, completed, error
    operation: null // 'invoice_update' or 'sync'
  });

  // Estado para progreso de sincronización en tiempo real
  const [syncProgress, setSyncProgress] = useState({
    processed: 0,
    total: 0,
    percentage: 0,
    phase: 'main',
    current: { ordersUpdated: 0, ordersCreated: 0, errors: 0, retried: 0 }
  });

  // Hooks
  const toast = useToast();

  // Colores para el tema
  const bgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  // NOTA: no hay un useEffect reactivo a pagination/filters aquí a propósito —
  // handlePaginationChange, handleFiltersChange y handleSortChange ya hacen su
  // propio fetch explícito con los valores correctos. Un efecto adicional que
  // reaccionara a esos mismos cambios duplicaba cada petición (y loadPaymentsData
  // cambia de identidad en cada render por depender de `pagination`/`payments.total`,
  // lo que además disparaba fetches extra en el mount inicial).

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Solo cargar pagos principales - REMOVER summary y estadísticas
      const paymentsData = await getPaymentsWithFilters(filters, pagination);

      // setStats(statsData); // COMENTADO - No cargar estadísticas
      setPayments(paymentsData);
      // setOverduePayments(overdueData); // COMENTADO - No cargar pagos atrasados

    } catch (err) {
      console.error('Error loading billing data:', err);
      setError('Error al cargar los datos de pagos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentsData = useCallback(async (getAllPages = false) => {
    try {
      // Solo mostrar loading si no es para obtener todas las páginas (esto es selección)
      if (!getAllPages) {
        console.log('🔄 Admin Billing: Setting searchLoading to true');
        setSearchLoading(true);
      }
      
      // Si se solicitan todas las páginas, usar paginación especial
      const targetPagination = getAllPages 
        ? { page: 1, per_page: payments.total || -1 }
        : pagination;
      
      console.log('Billing: Loading payments with filters', {
        filters,
        pagination: targetPagination,
        getAllPages,
        customer_id: filters.customer_id
      });
      
      const paymentsData = await getPaymentsWithFilters(filters, targetPagination);
      
      // Si es para obtener todas las páginas, retornar los datos sin actualizar el estado
      if (getAllPages) {
        console.log('🔍 BILLING: Todas las páginas obtenidas para selección:', {
          total: paymentsData.total,
          data_count: paymentsData.data?.length || 0
        });
        return paymentsData;
      }
      
      setPayments(paymentsData);
    } catch (err) {
      console.error('Error loading payments:', err);
      if (getAllPages) {
        throw err; // Re-throw para que PaymentTable pueda manejar el error
      }
    } finally {
      // Solo resetear loading si no es para obtener todas las páginas
      if (!getAllPages) {
        console.log('✅ Admin Billing: Setting searchLoading to false');
        setSearchLoading(false);
      }
    }
  }, [filters, pagination, payments.total]);

  const handleFiltersChange = async (newFilters) => {
    console.log('Billing: Received filters change (SEARCH TRIGGER)', {
      newFilters,
      customer_id: newFilters.customer_id,
      previousFilters: filters,
      message: 'Filters already saved by PaymentFilters'
    });
    
    const newPagination = { ...pagination, page: 1 };
    
    // Actualizar estados (NO guardar en localStorage - ya lo hizo PaymentFilters)
    setFilters(newFilters);
    setPagination(newPagination);
    
    console.log('Billing: Executing search with updated filters');
    
    // Ejecutar búsqueda directamente con los nuevos filtros
    setSearchLoading(true);
    try {
      console.log('Billing: Loading payments with search filters', {
        newFilters,
        newPagination,
        customer_id: newFilters.customer_id
      });
      
      const paymentsData = await getPaymentsWithFilters(newFilters, newPagination);
      setPayments(paymentsData);
    } catch (err) {
      console.error('Error loading payments during search:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePaginationChange = async (newPagination) => {
    console.log('Billing: Pagination change requested', {
      current: pagination,
      new: newPagination
    });
    
    // Actualizar estado de paginación
    setPagination(newPagination);
    
    // Guardar configuración
    saveBillingConfig({ filters, pagination: newPagination });
    
    // Cargar datos inmediatamente con la nueva paginación
    setSearchLoading(true);
    try {
      console.log('🔍 BILLING: Cargando datos con nueva paginación:', {
        filters,
        newPagination,
        filters_orderby: filters.orderby,
        filters_order: filters.order
      });
      
      const paymentsData = await getPaymentsWithFilters(filters, newPagination);
      setPayments(paymentsData);
      console.log('🔍 BILLING: Datos cargados:', {
        page: newPagination.page,
        per_page: newPagination.per_page,
        total: paymentsData.total,
        total_pages: paymentsData.total_pages,
        data_count: paymentsData.data?.length || 0,
        response_structure: paymentsData
      });
    } catch (err) {
      console.error('Error loading payments during pagination:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Manejar cambio de ordenamiento
  const handleSortChange = (sortField, sortOrder) => {
    const newFilters = { 
      ...filters, 
      orderby: sortField, 
      order: sortOrder 
    };
    
    // Resetear a página 1 al cambiar ordenamiento
    const newPagination = { ...pagination, page: 1 };
    
    // Actualizar estados
    setFilters(newFilters);
    setPagination(newPagination);
    
    // Guardar configuración
    saveBillingConfig({ filters: newFilters, pagination: newPagination });
    
    // Ejecutar búsqueda con nuevo ordenamiento
    handleFiltersChange(newFilters);
  };

  const handleExport = async (format, selectedOrderIds = null, customFilters = null) => {
    try {
      setIsExporting(true);

      const isSelectionExport = selectedOrderIds && selectedOrderIds.length > 0;
      const isCustomFilters = customFilters !== null;
      const exportFilters = customFilters || filters;
      const exportCount = isSelectionExport ? selectedOrderIds.length : (payments.total || 0);

      let description;
      if (isSelectionExport) {
        description = `Preparando ${exportCount} órdenes seleccionadas en formato ${format.toUpperCase()}...`;
      } else if (isCustomFilters) {
        const periodDesc = customFilters.start_date === customFilters.end_date 
          ? `del ${customFilters.start_date}`
          : `período personalizado`;
        description = `Preparando datos ${periodDesc} en formato ${format.toUpperCase()}...`;
      } else {
        description = `Preparando datos en formato ${format.toUpperCase()}...`;
      }

      toast({
        title: 'Iniciando exportación',
        description: description,
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      const result = await exportPaymentData(format, exportFilters, selectedOrderIds);

      toast({
        title: 'Exportación completada',
        description: `Se han exportado ${result.recordCount} registros en formato ${result.format}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Error en la exportación',
        description: error.message || 'No se pudo completar la exportación',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSync = async (currentFilters = filters, orderIds = null, useFilters = false) => {
    try {
      let description;
      let totalOrders;
      
      if (useFilters) {
        // Modo optimizado: sincronizar por filtros sin cargar todas las órdenes
        totalOrders = payments.total || 0;
        description = `todas las ${totalOrders.toLocaleString()} órdenes que coinciden con el filtro`;
      } else if (orderIds && orderIds.length > 0) {
        // Sincronizar órdenes específicas
        totalOrders = orderIds.length;
        description = `${totalOrders} orden${totalOrders !== 1 ? 'es' : ''} seleccionada${totalOrders !== 1 ? 's' : ''}`;
      } else {
        // Sincronizar todas las órdenes con filtros actuales (modo tradicional)
        totalOrders = payments.total || 0;
        description = `${totalOrders} orden${totalOrders !== 1 ? 'es' : ''} con los filtros actuales`;
      }

      if (totalOrders === 0) {
        toast({
          title: 'Sin órdenes para sincronizar',
          description: orderIds ? 'No hay órdenes seleccionadas' : 'No hay órdenes con los filtros actuales',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Confirmación ya se hace en PaymentTable, proceder directamente

      let finalOrderIds;
      
      if (useFilters) {
        // Modo optimizado: NO cargar todas las órdenes, procesar por filtros en backend
        toast({
          title: '⚡ Sincronización optimizada iniciada',
          description: `Procesando ${description} mediante filtros de base de datos...`,
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        
        // TODO: Implementar endpoint de sincronización por filtros
        // Por ahora usar el método tradicional pero con advertencia
        toast({
          title: '⚠️ Procesamiento en curso',
          description: 'Obteniendo órdenes para sincronización... Esto puede tomar unos minutos.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        
        const allOrders = await getPaymentsWithFilters(currentFilters, { page: 1, per_page: totalOrders });
        finalOrderIds = allOrders.data.map(order => order.id);
      } else if (orderIds && orderIds.length > 0) {
        // Usar IDs específicos proporcionados
        finalOrderIds = orderIds;
      } else {
        // Obtener TODAS las órdenes con los filtros actuales (no solo la página visible)
        toast({
          title: 'Obteniendo órdenes...',
          description: `Preparando ${totalOrders} órdenes para sincronización...`,
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
        
        const allOrders = await getPaymentsWithFilters(currentFilters, { page: 1, per_page: totalOrders });
        finalOrderIds = allOrders.data.map(order => order.id);
      }

      console.log('📋 SYNC: IDs a sincronizar:', finalOrderIds.length, finalOrderIds.slice(0, 5));

      toast({
        title: 'Sincronización iniciada',
        description: `Procesando ${finalOrderIds.length} órdenes con hooks nativos...`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Usar trigger_order_status_change para garantizar hooks y webhooks
      const result = await triggerStatusChangeBatch(finalOrderIds, {
        batchSize: 20,
        delayMs: 300,
        progressCallback: (processed, total, successful, errorCount) => {
          if (processed % 5 === 0 || processed === total) { // Actualizar cada 5 órdenes o al final
            console.log(`📊 Progreso: ${processed}/${total} - Exitosas: ${successful} - Errores: ${errorCount}`);
          }
        }
      });

      if (result.success) {
        toast({
          title: 'Sincronización completada',
          description: result.message,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Recargar datos para ver cambios
        await loadPaymentsData();
      } else {
        toast({
          title: 'Error en sincronización',
          description: result.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: 'Error en sincronización',
        description: error.message || 'No se pudo completar la sincronización',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Función para procesar grandes volúmenes con progreso visual
  const processLargeVolumeWithProgress = async (currentFilters, operation) => {
    const chunkSize = 50; // Chunks más pequeños para mejor progreso
    let offset = 0;
    let totalProcessed = 0;
    let totalUpdated = 0;
    let totalErrors = 0;
    let totalEstimated = payments.total || 0;
    
    // Inicializar progreso
    setMassiveProgress({
      isActive: true,
      current: 0,
      total: totalEstimated,
      percentage: 0,
      currentChunk: 0,
      totalChunks: Math.ceil(totalEstimated / chunkSize),
      errors: [],
      completed: 0,
      status: 'processing',
      operation: operation
    });
    
    try {
      let hasMore = true;
      let chunkNumber = 1;
      
      while (hasMore) {
        // Actualizar progreso del chunk actual
        setMassiveProgress(prev => ({
          ...prev,
          currentChunk: chunkNumber,
          status: 'processing'
        }));
        
        // Procesar chunk actual
        const result = await bulkUpdateInvoiceNumbers({
          start_date: currentFilters.start_date,
          end_date: currentFilters.end_date,
          status: currentFilters.status || 'completed',
          limit: chunkSize,
          offset: offset,
          force_update: false,
          chunked_mode: true
        });
        
        if (result.success) {
          // Debug: verificar estructura de respuesta
          console.log('🔍 DEBUG: Estructura de respuesta del chunk:', result);
          
          // Corregir acceso a los datos - el endpoint devuelve result.data
          const chunkResults = result.data?.results || result.results || { processed: 0, updated: 0, errors: 0 };
          
          totalProcessed += chunkResults.processed || 0;
          totalUpdated += chunkResults.updated || 0;
          totalErrors += chunkResults.errors || 0;
          
          // Actualizar progreso
          const percentage = Math.min(Math.round((totalProcessed / totalEstimated) * 100), 100);
          
          setMassiveProgress(prev => ({
            ...prev,
            current: totalProcessed,
            completed: totalUpdated,
            percentage: percentage,
            errors: [...prev.errors, ...(chunkResults.details?.filter(d => d.status === 'error') || [])]
          }));
          
          // Mostrar progreso cada chunk
          toast({
            title: `📊 Progreso: ${percentage}%`,
            description: `Chunk ${chunkNumber}: ${chunkResults.updated} actualizadas, ${chunkResults.errors} errores`,
            status: chunkResults.errors > 0 ? 'warning' : 'info',
            duration: 2000,
            isClosable: true,
          });
          
          // Verificar si hay más datos
          hasMore = chunkResults.processed === chunkSize;
          offset += chunkSize;
          chunkNumber++;
          
          // Pequeña pausa para no saturar el servidor
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          hasMore = false;
          throw new Error(result.message || 'Error en procesamiento');
        }
      }
      
      // Completado
      setMassiveProgress(prev => ({
        ...prev,
        status: 'completed',
        percentage: 100
      }));
      
      // Auto-limpiar progreso después de 5 segundos
      setTimeout(() => {
        setMassiveProgress({
          isActive: false,
          current: 0,
          total: 0,
          percentage: 0,
          currentChunk: 0,
          totalChunks: 0,
          errors: [],
          completed: 0,
          status: 'idle',
          operation: null
        });
      }, 5000);
      
      return {
        success: true,
        message: `Proceso completado: ${totalUpdated} órdenes actualizadas de ${totalProcessed} procesadas`,
        results: {
          processed: totalProcessed,
          updated: totalUpdated,
          errors: totalErrors
        }
      };
      
    } catch (error) {
      setMassiveProgress(prev => ({
        ...prev,
        status: 'error'
      }));
      
      throw error;
    }
  };

  // Función para manejar actualización masiva de números de boleta
  const handleUpdateInvoiceNumbers = async (currentFilters = filters, orderIds = null, useFilters = false) => {
    try {
      console.log('🧾 INVOICE UPDATE: Iniciando actualización', { currentFilters, orderIds, useFilters });

      // Descripción más clara para el usuario
      const description = useFilters 
        ? `Procesando todas las órdenes que coinciden con el filtro actual...`
        : orderIds 
          ? `Procesando ${orderIds.length} órdenes seleccionadas...` 
          : 'Procesando órdenes con BillMySales ID...';

      toast({
        title: '🔄 Actualizando números de boleta',
        description: description,
        status: 'info',
        duration: 4000,
        isClosable: true,
      });

      let result;
      
      if (useFilters) {
        // Modo optimizado: usar chunking desde frontend para progreso visual
        result = await processLargeVolumeWithProgress(currentFilters, 'invoice_update');
      } else {
        // Modo tradicional: usar IDs específicos o filtros con límite
        result = await bulkUpdateInvoiceNumbers({
          start_date: currentFilters.start_date,
          end_date: currentFilters.end_date,
          status: currentFilters.status || 'completed',
          order_ids: orderIds, // Si hay IDs específicos los incluye
          limit: orderIds && orderIds.length > 0 ? orderIds.length : 100,
          force_update: false
        });
      }

      if (result.success) {
        toast({
          title: 'Actualización completada',
          description: result.message,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Recargar datos para mostrar los números actualizados
        await loadPaymentsData();
      } else {
        toast({
          title: 'Error en actualización',
          description: result.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error actualizando números de boleta:', error);
      toast({
        title: 'Error',
        description: 'Error inesperado al actualizar números de boleta',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleNapsisSync = async () => {
    // Reset del estado de NAPSIS
    setNapsisStep(0);
    setNapsisData(null);
    setComparisonResult(null);
    setSyncResult(null);
    setIsExecutingSync(false);
    
    // Reset de estados de selección
    setSelectedActions(new Set());
    setSelectAllUpdates(true);
    setSelectAllCreations(true);
    
    setNapsisModalOpen(true);
    
    // Cargar customers si no están cargados
    if (customers.length === 0) {
      try {
        const customersData = await getCustomers('', '', '', '', '', '', 1, -1); // -1 = Obtener TODOS los customers
        setCustomers(customersData.data || []);
      } catch (error) {
        console.error('Error cargando customers:', error);
        toast({
          title: "Error cargando datos",
          description: "No se pudieron cargar los datos de usuarios",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleNapsisDataImported = (data) => {
    setNapsisData(data);
    setNapsisStep(1);
    
    toast({
      title: "Datos NAPSIS importados",
      description: `Se importaron ${data.length} estudiantes`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleComparisonComplete = (result) => {
    setComparisonResult(result);
    
    if (result.syncNeeded.length > 0) {
      // Inicializar selección de acciones - todas seleccionadas por defecto
      const allActionIds = new Set();
      result.syncNeeded.forEach((comparison, compIndex) => {
        comparison.syncActions.forEach((action, actionIndex) => {
          allActionIds.add(`${compIndex}-${actionIndex}`);
        });
      });
      setSelectedActions(allActionIds);
      setSelectAllUpdates(true);
      setSelectAllCreations(true);
      
      setNapsisStep(2); // Ir al paso de revisión
    } else {
      // Si no hay nada que sincronizar, saltar al paso de resultados
      setNapsisStep(4);
      setSyncResult({
        success: [],
        failed: [],
        statistics: {
          ordersUpdated: 0,
          ordersCreated: 0,
          totalProcessed: 0,
          errors: 0
        },
        message: "No se requiere sincronización"
      });
    }
  };

  // Funciones para manejar la selección de acciones
  const handleActionToggle = (actionId) => {
    const newSelected = new Set(selectedActions);
    if (newSelected.has(actionId)) {
      newSelected.delete(actionId);
    } else {
      newSelected.add(actionId);
    }
    setSelectedActions(newSelected);
  };

  const handleSelectAllByType = (type, isSelected) => {
    const newSelected = new Set(selectedActions);
    
    comparisonResult.syncNeeded.forEach((comparison, compIndex) => {
      comparison.syncActions.forEach((action, actionIndex) => {
        const actionId = `${compIndex}-${actionIndex}`;
        const isUpdateAction = action.action === 'update_status';
        
        if ((type === 'updates' && isUpdateAction) || (type === 'creations' && !isUpdateAction)) {
          if (isSelected) {
            newSelected.add(actionId);
          } else {
            newSelected.delete(actionId);
          }
        }
      });
    });
    
    setSelectedActions(newSelected);
    
    if (type === 'updates') {
      setSelectAllUpdates(isSelected);
    } else {
      setSelectAllCreations(isSelected);
    }
  };

  const getSelectedActionsForSync = () => {
    if (!comparisonResult) return [];
    
    const selectedSyncActions = [];
    comparisonResult.syncNeeded.forEach((comparison, compIndex) => {
      const selectedActionsForComparison = comparison.syncActions.filter((action, actionIndex) => {
        return selectedActions.has(`${compIndex}-${actionIndex}`);
      });
      
      if (selectedActionsForComparison.length > 0) {
        selectedSyncActions.push({
          ...comparison,
          syncActions: selectedActionsForComparison
        });
      }
    });
    
    return selectedSyncActions;
  };

  // Función para generar preview de peticiones HTTP antes de la ejecución
  const generateRequestPreview = (selectedSyncActions) => {
    // Usar la misma función que paymentSync.js
    const credentials = getCredentials();
    const apiUrl = getConfig(credentials, "WP_API_URL") || "https://pagos.blaspascal.cl/wp-json";
    
    console.log('🔧 VISTA PREVIA - Usando configuración REAL:', {
      apiUrl: apiUrl,
      wooApiUrl: getConfig(credentials, "WOO_API_URL"),
      wpApiUrl: getConfig(credentials, "WP_API_URL"),
      credentialsType: typeof credentials,
      hasConfigs: !!credentials.configs
    });
    
    const requestPreview = [];
    
    selectedSyncActions.forEach((comparison, compIndex) => {
      comparison.syncActions.forEach((action, actionIndex) => {
        let request = {
          id: `${compIndex}-${actionIndex}`,
          customer: comparison.customer,
          action: action,
          method: 'POST',
          endpoint: '',
          payload: {},
          description: ''
        };
        
        if (action.action === 'update_status') {
          request.endpoint = `${apiUrl}/datofy/v1/update-order-status`;
          request.payload = {
            order_id: action.orderId,
            new_status: action.toStatus,
            sync_source: 'napsis',
            // FECHAS HISTÓRICAS: Usar mismo parámetro que ejecuteSyncActions
            historical_date: action.napsisPago?.boleta?.fechaFormateada || action.napsisPago?.fechaPago
          };
          request.description = `Actualizar orden ${action.orderId} de "${action.fromStatus}" a "${action.toStatus}" con fecha histórica`;
          request.type = 'update';
          request.boleta = action.napsisPago?.numeroBoleta;
          request.configUrl = apiUrl; // Para debug
          
        } else if (action.action === 'create_order') {
          request.endpoint = `${apiUrl}/datofy/v1/create-napsis-order`;
          request.payload = {
            customer_id: comparison.customer.id,
            // PARÁMETROS REQUERIDOS por el endpoint  
            mes: action.mes,
            concepto: action.concepto,
            estado: action.estado,
            sync_source: 'napsis',
            // DATOS ADICIONALES
            napsis_data: action.napsisPago,
            // FECHAS HISTÓRICAS: Usar mismo parámetro que ejecuteSyncActions
            historical_date: action.napsisPago?.boleta?.fechaFormateada || action.napsisPago?.fechaPago
          };
          request.description = `Crear nueva orden para ${action.mes} con fecha histórica`;
          request.type = 'create_order';
          request.boleta = action.napsisPago?.numeroBoleta;
          request.configUrl = apiUrl; // Para debug
          
        } else if (action.action === 'create_fine') {
          request.endpoint = `${apiUrl}/datofy/v1/create-fine-direct`;
          request.payload = {
            customer_id: comparison.customer.id,
            // PARÁMETROS REQUERIDOS por el endpoint
            mes: action.mes, // Usar formato YYYY-MM
            concepto: `Multa por atraso - ${action.multaData?.concepto}`,
            estado: 'completed',
            sync_source: 'napsis',
            // FECHAS HISTÓRICAS: Usar mismo parámetro que ejecuteSyncActions
            historical_date: action.napsisPago?.boleta?.fechaFormateada || action.multaData?.fechaPago,
            meta_data: [
              { key: 'mes', value: action.mes }, // Usar formato YYYY-MM
              { key: 'concepto', value: `Multa por atraso - ${action.multaData?.concepto}` },
              { key: 'tipo_orden', value: 'multa' },
              { key: 'cantidad_multas', value: action.multaData?.cantidadMultas },
              { key: 'dias_atraso', value: action.multaData?.diasAtraso || 0 },
              { key: 'sync_source', value: 'napsis' }
            ],
            line_items: [{
              name: `Multa - ${action.mes}`,
              quantity: action.multaData?.cantidadMultas || 1
            }]
          };
          request.description = `Crear multa: ${action.multaData?.cantidadMultas} multa(s) por ${action.multaData?.mes} con fecha histórica`;
          request.type = 'create_fine';
          request.boleta = action.napsisPago?.numeroBoleta;
          request.configUrl = apiUrl; // Para debug
        }
        
        requestPreview.push(request);
      });
    });
    
    return requestPreview;
  };

  const executeNapsisSync = async () => {
    const selectedSyncActions = getSelectedActionsForSync();
    
    if (selectedSyncActions.length === 0) {
      toast({
        title: "No hay acciones seleccionadas",
        description: "Debe seleccionar al menos una acción para ejecutar la sincronización",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setIsExecutingSync(true);
    
    // Resetear progreso
    setSyncProgress({
      processed: 0,
      total: 0,
      percentage: 0,
      phase: 'main',
      current: { ordersUpdated: 0, ordersCreated: 0, errors: 0, retried: 0 }
    });
    
    try {
      const result = await paymentSyncService.executeSyncActions(
        selectedSyncActions,
        (progressData) => {
          setSyncProgress({
            processed: progressData.processed,
            total: progressData.total,
            percentage: progressData.percentage,
            phase: progressData.processed > progressData.total ? 'retry' : 'main',
            current: progressData.current
          });
        }
      );
      setSyncResult(result);
      setNapsisStep(4);
      
      const successCount = result.statistics.ordersUpdated + result.statistics.ordersCreated;
      const hasErrors = result.statistics.errors > 0;
      const hasRetries = result.statistics.retried > 0;
      
      let description = `${successCount} acciones ejecutadas exitosamente`;
      if (hasRetries) {
        description += ` (${result.statistics.retried} recuperadas tras fallos de red)`;
      }
      if (hasErrors) {
        description += ` (${result.statistics.errors} errores definitivos)`;
      }
      
      toast({
        title: "Sincronización completada",
        description: description,
        status: hasErrors ? "warning" : hasRetries ? "info" : "success",
        duration: 8000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error ejecutando sincronización:', error);
      
      setSyncResult({
        success: [],
        failed: [],
        statistics: {
          ordersUpdated: 0,
          ordersCreated: 0,
          totalProcessed: 0,
          errors: 1
        },
        error: error.message
      });
      
      toast({
        title: "Error en la sincronización",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExecutingSync(false);
    }
  };

  if (loading) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="60vh"
        fontFamily="jakarta"
      >
        <Spinner size="xl" color="coral.500" thickness="4px" />
        <Text mt={4} color="ink.700">Cargando datos de pagos...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" fontFamily="jakarta">
        <Alert status="error" borderRadius="10px">
          <AlertIcon />
          <Box>
            <AlertTitle>Error al cargar datos</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      </Flex>
    );
  }

  return (
    <Flex direction="column" maxW="100%" overflowX="hidden" fontFamily="jakarta">
      {/* ESTADÍSTICAS PRINCIPALES REMOVIDAS PARA MEJORAR RENDIMIENTO */}
      {/* <Grid 
        templateColumns={{ 
          base: "1fr", 
          md: "repeat(2, 1fr)", 
          lg: "repeat(4, 1fr)" 
        }} 
        gap={6} 
        mb={8}
      >
        <PaymentStatistics
          key="total-collected"
          icon={<Icon h="24px" w="24px" color="white" as={FaWallet} />}
          title="Total Recaudado"
          description={`Año ${new Date().getFullYear()}`}
          amount={stats.total_collected || 0}
          color="blue"
        />
        <PaymentStatistics
          key="payments-completed"
          icon={<Icon h="24px" w="24px" color="white" as={FaCheckCircle} />}
          title="Pagos Completados"
          description={`${stats.total_orders || 0} órdenes`}
          amount={stats.total_collected || 0}
          color="green"
        />
        <PaymentStatistics
          key="payments-pending"
          icon={<Icon h="24px" w="24px" color="white" as={FaHourglassHalf} />}
          title="Pagos Pendientes"
          description={`${stats.pending_orders || 0} órdenes`}
          amount={stats.pending_amount || 0}
          color="orange"
        />
        <PaymentStatistics
          key="payments-overdue"
          icon={<Icon h="24px" w="24px" color="white" as={FaExclamationTriangle} />}
          title="Seguimiento Requerido"
          description={`${stats.overdue_orders || 0} con atraso`}
          amount={stats.overdue_amount || 0}
          color="red"
        />
      </Grid> */}

      {/* Filtros Avanzados */}
      <Box mb={6}>
        <PaymentFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          // onStatsReload REMOVIDO - No cargar estadísticas
          onStatsReload={null}
          isLoading={searchLoading}
        />
      </Box>


      {/* Indicador de Progreso Masivo */}
      {massiveProgress.isActive && (
        <Box mb={6} p={4} bg="coral.tint" borderRadius="14px" border="1.5px solid" borderColor="coral.soft">
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="lg" fontWeight="800" color="coral.700">
                🔄 {massiveProgress.operation === 'invoice_update' ? 'Actualizando números de boleta' : 'Sincronizando órdenes'} - Procesamiento Masivo
              </Text>
              <Text fontSize="md" color="coral.700" fontWeight="600">
                {massiveProgress.percentage}% ({massiveProgress.current.toLocaleString()} / {massiveProgress.total.toLocaleString()})
              </Text>
            </HStack>

            <Progress
              value={massiveProgress.percentage}
              colorScheme="orange"
              size="lg"
              hasStripe
              isAnimated
              borderRadius="8px"
            />

            <HStack justify="space-between" fontSize="sm">
              <HStack spacing={4}>
                <HStack spacing={1}>
                  <Text color="ink.700" fontWeight="600">📦 Chunk:</Text>
                  <Text color="ink.700">{massiveProgress.currentChunk} / {massiveProgress.totalChunks}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Text color="ok.500" fontWeight="600">✅ Completadas:</Text>
                  <Text color="ok.500">{massiveProgress.completed.toLocaleString()}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Text color="danger.500" fontWeight="600">❌ Errores:</Text>
                  <Text color="danger.500">{massiveProgress.errors.length}</Text>
                </HStack>
              </HStack>

              <Text color="ink.700" fontStyle="italic">
                {massiveProgress.status === 'processing' ? 'Procesando...' :
                 massiveProgress.status === 'completed' ? '✅ Completado' :
                 massiveProgress.status === 'error' ? '❌ Error' : 'Iniciando...'}
              </Text>
            </HStack>

            {massiveProgress.errors.length > 0 && (
              <Box bg="danger.bg" p={3} borderRadius="10px" border="1px solid" borderColor="danger.500">
                <Text fontSize="sm" color="danger.500" fontWeight="600" mb={2}>
                  ⚠️ Últimos errores encontrados:
                </Text>
                {massiveProgress.errors.slice(-3).map((error, index) => (
                  <Text key={index} fontSize="xs" color="danger.500" ml={2}>
                    • Orden #{error.order_id}: {error.message}
                  </Text>
                ))}
                {massiveProgress.errors.length > 3 && (
                  <Text fontSize="xs" color="danger.500" ml={2} fontStyle="italic">
                    ... y {massiveProgress.errors.length - 3} errores más
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </Box>
      )}

      {/* Contenido Principal */}
      <Box>
        <PaymentTable
          payments={payments}
          filters={filters}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          onExport={handleExport}
          isExporting={isExporting}
          onRefresh={loadPaymentsData}
          onSortChange={handleSortChange}
          onSync={handleSync}
          onUpdateInvoiceNumbers={handleUpdateInvoiceNumbers}
          isLoading={searchLoading}
        />
      </Box>

      {/* INFORMACIÓN DE PERÍODO REMOVIDA */}
      {/* {stats.period && (
        <Box mt={6} p={4} bg={bgColor} borderRadius="md" border="1px solid" borderColor="gray.200">
          <Flex align="center" justify="space-between" flexWrap="wrap">
            <Text color={textColor} fontSize="sm">
              <Icon as={MdTrendingUp} mr={2} />
              Período Actual: Año {new Date().getFullYear()} ({new Date(stats.period.start_date).toLocaleDateString('es-CL')} - {new Date(stats.period.end_date).toLocaleDateString('es-CL')})
            </Text>
            <Text color="gray.500" fontSize="sm">
              Última actualización: {new Date().toLocaleString('es-CL')}
            </Text>
          </Flex>
        </Box>
      )} */}

      {/* Modal de Sincronización NAPSIS */}
      <Modal isOpen={napsisModalOpen} onClose={() => setNapsisModalOpen(false)} size="full">
        <ModalOverlay />
        <ModalContent maxW="95vw" maxH="95vh" overflow="hidden">
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FiRefreshCw} color="blue.500" boxSize={6} />
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold">
                  Sincronización de Pagos NAPSIS
                </Text>
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  Comparar y sincronizar datos de pagos con el sistema NAPSIS
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isExecutingSync} />
          <ModalBody pb={6} overflow="auto" maxH="calc(95vh - 200px)">
            <VStack spacing={6} align="stretch">
              
              {/* Stepper de progreso */}
              <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.200">
                <HStack spacing={0} w="100%">
                  {["Importar", "Comparar", "Revisar", "Sincronizar", "Resultados"].map((step, index) => (
                    <HStack key={index} flex={1} spacing={3}>
                      <VStack spacing={1}>
                        <Box
                          w={10}
                          h={10}
                          borderRadius="full"
                          bg={index <= napsisStep ? "blue.500" : "gray.300"}
                          color="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontSize="sm"
                          fontWeight="bold"
                        >
                          {index < napsisStep ? "✓" : index + 1}
                        </Box>
                        <Text fontSize="xs" fontWeight="medium" textAlign="center">
                          {step}
                        </Text>
                      </VStack>
                      {index < 4 && (
                        <Box
                          flex={1}
                          h="2px"
                          bg={index < napsisStep ? "blue.500" : "gray.300"}
                          mx={2}
                        />
                      )}
                    </HStack>
                  ))}
                </HStack>
              </Box>

              {/* Contenido según el paso */}
              {napsisStep === 0 && (
                <NapsisImporter
                  onDataImported={handleNapsisDataImported}
                  onError={(error) => {
                    toast({
                      title: "Error en importación",
                      description: error.message,
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                  }}
                />
              )}

              {napsisStep === 1 && napsisData && (
                <SyncComparator
                  napsisData={napsisData}
                  customers={customers}
                  onComparisonComplete={handleComparisonComplete}
                  onError={(error) => {
                    toast({
                      title: "Error en comparación",
                      description: error.message,
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                  }}
                />
              )}

              {napsisStep === 2 && comparisonResult && (() => {
                // Calcular estadísticas de acciones
                const allActions = comparisonResult.syncNeeded.flatMap((comp, compIndex) => 
                  comp.syncActions.map((action, actionIndex) => ({
                    ...action,
                    customer: comp.customer,
                    actionId: `${compIndex}-${actionIndex}`,
                    compIndex,
                    actionIndex
                  }))
                );
                
                const updateActions = allActions.filter(action => action.action === 'update_status');
                const createActions = allActions.filter(action => action.action !== 'update_status');
                const multaActions = allActions.filter(action => action.action === 'create_fine');
                const paymentActions = allActions.filter(action => action.action === 'create_order');
                
                console.log(`📋 PASO REVISAR: ${multaActions.length} multas detectadas`, multaActions.map(a => a.customer?.first_name));
                
                const selectedCount = selectedActions.size;
                const selectedUpdateCount = updateActions.filter(action => selectedActions.has(action.actionId)).length;
                const selectedCreateCount = createActions.filter(action => selectedActions.has(action.actionId)).length;
                
                return (
                  <VStack spacing={6} align="stretch">
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <VStack align="start" spacing={1} flex={1}>
                        <AlertTitle>Revisar Acciones de Sincronización</AlertTitle>
                        <AlertDescription>
                          Selecciona las acciones que deseas ejecutar. Se han detectado {allActions.length} acciones pendientes.
                        </AlertDescription>
                      </VStack>
                    </Alert>

                    {/* Estadísticas generales */}
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                      <Stat bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.200">
                        <StatLabel fontSize="xs" color="blue.600">Actualizar Órdenes</StatLabel>
                        <StatNumber fontSize="lg" color="blue.700">{selectedUpdateCount} / {updateActions.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>Cambiar estado a completado</StatHelpText>
                      </Stat>
                      <Stat bg="green.50" p={3} borderRadius="md" border="1px solid" borderColor="green.200">
                        <StatLabel fontSize="xs" color="green.600">Crear Mensualidades</StatLabel>
                        <StatNumber fontSize="lg" color="green.700">{paymentActions.filter(a => selectedActions.has(a.actionId)).length} / {paymentActions.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>Nuevas órdenes completed</StatHelpText>
                      </Stat>
                      <Stat bg="red.50" p={3} borderRadius="md" border="1px solid" borderColor="red.200">
                        <StatLabel fontSize="xs" color="red.600">Crear Multas</StatLabel>
                        <StatNumber fontSize="lg" color="red.700">{multaActions.filter(a => selectedActions.has(a.actionId)).length} / {multaActions.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>Interés por mora</StatHelpText>
                      </Stat>
                      <Stat bg="purple.50" p={3} borderRadius="md" border="1px solid" borderColor="purple.200">
                        <StatLabel fontSize="xs" color="purple.600">Total Seleccionado</StatLabel>
                        <StatNumber fontSize="lg" color="purple.700">{selectedCount} / {allActions.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>Acciones a ejecutar</StatHelpText>
                      </Stat>
                    </SimpleGrid>

                    {/* Sección de Actualización de Órdenes Existentes */}
                    {updateActions.length > 0 && (
                      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between" align="center">
                            <HStack spacing={3}>
                              <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
                                🔄 ACTUALIZAR ÓRDENES EXISTENTES
                              </Badge>
                              <Text fontSize="sm" color="blue.600">
                                {updateActions.length} órden{updateActions.length !== 1 ? 'es' : ''} pendiente{updateActions.length !== 1 ? 's' : ''} → completadas
                              </Text>
                            </HStack>
                            <Checkbox
                              isChecked={selectAllUpdates}
                              onChange={(e) => handleSelectAllByType('updates', e.target.checked)}
                              colorScheme="blue"
                              size="lg"
                            >
                              Seleccionar todas
                            </Checkbox>
                          </HStack>
                          
                          <Box maxH="300px" overflow="auto">
                            <Table size="sm" variant="simple">
                              <Thead bg="blue.50">
                                <Tr>
                                  <Th w="40px"></Th>
                                  <Th>Usuario</Th>
                                  <Th>Mes</Th>
                                  <Th>Estado Actual</Th>
                                  <Th>Nuevo Estado</Th>
                                  <Th>ID Orden</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {updateActions.map((action) => (
                                  <Tr key={action.actionId} bg={selectedActions.has(action.actionId) ? "blue.25" : "white"}>
                                    <Td>
                                      <Checkbox
                                        isChecked={selectedActions.has(action.actionId)}
                                        onChange={() => handleActionToggle(action.actionId)}
                                        colorScheme="blue"
                                      />
                                    </Td>
                                    <Td fontWeight="medium">
                                      {action.customer.first_name} {action.customer.last_name}
                                    </Td>
                                    <Td>{action.mes}</Td>
                                    <Td>
                                      <Badge colorScheme="orange" size="sm">{action.fromStatus}</Badge>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme="green" size="sm">{action.toStatus || 'completed'}</Badge>
                                    </Td>
                                    <Td fontSize="xs" fontFamily="mono" color="gray.600">
                                      #{action.orderId}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </VStack>
                      </Box>
                    )}

                    {/* Sección de Creación de Nuevas Órdenes */}
                    {createActions.length > 0 && (
                      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
                        <VStack spacing={4} align="stretch">
                          <HStack justify="space-between" align="center">
                            <HStack spacing={3}>
                              <Badge colorScheme="green" px={3} py={1} borderRadius="full">
                                ➕ CREAR NUEVAS ÓRDENES
                              </Badge>
                              <Text fontSize="sm" color="green.600">
                                {createActions.length} órden{createActions.length !== 1 ? 'es' : ''} nueva{createActions.length !== 1 ? 's' : ''} como completadas
                              </Text>
                            </HStack>
                            <Checkbox
                              isChecked={selectAllCreations}
                              onChange={(e) => handleSelectAllByType('creations', e.target.checked)}
                              colorScheme="green"
                              size="lg"
                            >
                              Seleccionar todas
                            </Checkbox>
                          </HStack>
                          
                          <Box maxH="300px" overflow="auto">
                            <Table size="sm" variant="simple">
                              <Thead bg="green.50">
                                <Tr>
                                  <Th w="40px"></Th>
                                  <Th>Usuario</Th>
                                  <Th>Mes</Th>
                                  <Th>Tipo</Th>
                                  <Th>Concepto</Th>
                                  <Th>Producto ID</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {paymentActions.map((action) => (
                                  <Tr key={action.actionId} bg={selectedActions.has(action.actionId) ? "green.25" : "white"}>
                                    <Td>
                                      <Checkbox
                                        isChecked={selectedActions.has(action.actionId)}
                                        onChange={() => handleActionToggle(action.actionId)}
                                        colorScheme="green"
                                      />
                                    </Td>
                                    <Td fontWeight="medium">
                                      {action.customer.first_name} {action.customer.last_name}
                                    </Td>
                                    <Td>{action.mes}</Td>
                                    <Td>
                                      <Badge colorScheme="green" size="sm">Mensualidad</Badge>
                                    </Td>
                                    <Td fontSize="sm">
                                      {action.customer.meta_data?.find(m => m.key === 'suscription_name')?.value || 'Mensualidad'}
                                    </Td>
                                    <Td fontSize="xs" fontFamily="mono" color="gray.600">
                                      Suscripción
                                    </Td>
                                  </Tr>
                                ))}
                                {multaActions.map((action) => (
                                  <Tr key={action.actionId} bg={selectedActions.has(action.actionId) ? "red.25" : "white"}>
                                    <Td>
                                      <Checkbox
                                        isChecked={selectedActions.has(action.actionId)}
                                        onChange={() => handleActionToggle(action.actionId)}
                                        colorScheme="red"
                                      />
                                    </Td>
                                    <Td fontWeight="medium">
                                      {action.customer.first_name} {action.customer.last_name}
                                    </Td>
                                    <Td>{action.multaData?.mes || action.mes}</Td>
                                    <Td>
                                      <Badge colorScheme="red" size="sm">Multa</Badge>
                                    </Td>
                                    <Td fontSize="sm" color="red.600">
                                      Interés por mora
                                    </Td>
                                    <Td fontSize="xs" fontFamily="mono" color="gray.600">
                                      #745
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </VStack>
                      </Box>
                    )}

                    <Divider />

                    <HStack spacing={3} justify="center">
                      <Button
                        variant="outline"
                        onClick={() => setNapsisStep(1)}
                      >
                        ← Volver a Comparar
                      </Button>
                      <Button
                        colorScheme="purple"
                        size="lg"
                        onClick={() => setNapsisStep(3)}
                        leftIcon={<Icon as={FiRefreshCw} />}
                        isDisabled={selectedCount === 0}
                      >
                        Ejecutar {selectedCount} Accion{selectedCount !== 1 ? 'es' : ''} →
                      </Button>
                    </HStack>
                  </VStack>
                );
              })()}

              {napsisStep === 3 && comparisonResult && (() => {
                const selectedSyncActions = getSelectedActionsForSync();
                const selectedCount = selectedActions.size;
                const requestPreview = generateRequestPreview(selectedSyncActions);
                
                // Agrupar peticiones por tipo
                const updateRequests = requestPreview.filter(r => r.type === 'update');
                const createOrderRequests = requestPreview.filter(r => r.type === 'create_order');
                const createFineRequests = requestPreview.filter(r => r.type === 'create_fine');
                
                return (
                  <VStack spacing={6} align="stretch">
                    <Alert status="warning">
                      <AlertIcon />
                      <VStack align="start" spacing={1} flex={1}>
                        <AlertTitle>Vista Previa de Peticiones HTTP</AlertTitle>
                        <AlertDescription>
                          Se ejecutarán {requestPreview.length} peticiones HTTP. Revisa los detalles antes de continuar.
                        </AlertDescription>
                      </VStack>
                    </Alert>

                    {/* Estadísticas de peticiones */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <Stat bg="blue.50" p={3} borderRadius="md" border="1px solid" borderColor="blue.200">
                        <StatLabel fontSize="xs" color="blue.600">🔄 Actualizar Órdenes</StatLabel>
                        <StatNumber fontSize="lg" color="blue.700">{updateRequests.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>POST update-order-status</StatHelpText>
                      </Stat>
                      <Stat bg="green.50" p={3} borderRadius="md" border="1px solid" borderColor="green.200">
                        <StatLabel fontSize="xs" color="green.600">➕ Crear Mensualidades</StatLabel>
                        <StatNumber fontSize="lg" color="green.700">{createOrderRequests.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>POST create-napsis-order (completed)</StatHelpText>
                      </Stat>
                      <Stat bg="red.50" p={3} borderRadius="md" border="1px solid" borderColor="red.200">
                        <StatLabel fontSize="xs" color="red.600">⚠️ Crear Multas</StatLabel>
                        <StatNumber fontSize="lg" color="red.700">{createFineRequests.length}</StatNumber>
                        <StatHelpText fontSize="xs" m={0}>POST create-fine-direct</StatHelpText>
                      </Stat>
                    </SimpleGrid>

                    {/* Preview detallado de peticiones por tipo */}
                    <Tabs variant="enclosed" colorScheme="blue">
                      <TabList>
                        <Tab fontSize="sm">🔄 Actualizar ({updateRequests.length})</Tab>
                        <Tab fontSize="sm">➕ Crear Mensualidades ({createOrderRequests.length})</Tab>
                        <Tab fontSize="sm">⚠️ Crear Multas ({createFineRequests.length})</Tab>
                        <Tab fontSize="sm">📋 Resumen General</Tab>
                      </TabList>

                      <TabPanels>
                        {/* Tab: Actualizar órdenes */}
                        <TabPanel p={0} pt={4}>
                          {updateRequests.length > 0 ? (
                            <Box maxH="400px" overflow="auto">
                              <VStack spacing={3} align="stretch">
                                {updateRequests.map((request, index) => (
                                  <Box key={request.id} bg="blue.25" p={4} borderRadius="md" border="1px solid" borderColor="blue.200">
                                    <VStack spacing={2} align="stretch">
                                      <HStack justify="space-between">
                                        <Text fontWeight="bold" color="blue.800">
                                          {request.customer.first_name} {request.customer.last_name} - {request.action.mes}
                                        </Text>
                                        {request.boleta && (
                                          <Badge colorScheme="blue" variant="solid">
                                            Boleta: {request.boleta}
                                          </Badge>
                                        )}
                                      </HStack>
                                      <Text fontSize="sm" color="blue.700">
                                        {request.description}
                                      </Text>
                                      <Box bg="white" p={3} borderRadius="md" border="1px solid" borderColor="blue.300">
                                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                                          {request.method} {request.endpoint}
                                        </Text>
                                        <Box bg="gray.50" p={2} borderRadius="md" fontFamily="mono" fontSize="xs">
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(request.payload, null, 2)}
                                          </pre>
                                        </Box>
                                      </Box>
                                    </VStack>
                                  </Box>
                                ))}
                              </VStack>
                            </Box>
                          ) : (
                            <Text color="gray.500" textAlign="center" py={8}>
                              No hay órdenes para actualizar
                            </Text>
                          )}
                        </TabPanel>

                        {/* Tab: Crear mensualidades */}
                        <TabPanel p={0} pt={4}>
                          {createOrderRequests.length > 0 ? (
                            <Box maxH="400px" overflow="auto">
                              <VStack spacing={3} align="stretch">
                                {createOrderRequests.map((request, index) => (
                                  <Box key={request.id} bg="green.25" p={4} borderRadius="md" border="1px solid" borderColor="green.200">
                                    <VStack spacing={2} align="stretch">
                                      <HStack justify="space-between">
                                        <Text fontWeight="bold" color="green.800">
                                          {request.customer.first_name} {request.customer.last_name} - {request.action.mes}
                                        </Text>
                                        {request.boleta && (
                                          <Badge colorScheme="green" variant="solid">
                                            Boleta: {request.boleta}
                                          </Badge>
                                        )}
                                      </HStack>
                                      <Text fontSize="sm" color="green.700">
                                        {request.description}
                                      </Text>
                                      <Box bg="white" p={3} borderRadius="md" border="1px solid" borderColor="green.300">
                                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                                          {request.method} {request.endpoint}
                                        </Text>
                                        <Box bg="gray.50" p={2} borderRadius="md" fontFamily="mono" fontSize="xs">
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(request.payload, null, 2)}
                                          </pre>
                                        </Box>
                                      </Box>
                                    </VStack>
                                  </Box>
                                ))}
                              </VStack>
                            </Box>
                          ) : (
                            <Text color="gray.500" textAlign="center" py={8}>
                              No hay mensualidades para crear
                            </Text>
                          )}
                        </TabPanel>

                        {/* Tab: Crear multas */}
                        <TabPanel p={0} pt={4}>
                          {createFineRequests.length > 0 ? (
                            <Box maxH="400px" overflow="auto">
                              <VStack spacing={3} align="stretch">
                                {createFineRequests.map((request, index) => (
                                  <Box key={request.id} bg="red.25" p={4} borderRadius="md" border="1px solid" borderColor="red.200">
                                    <VStack spacing={2} align="stretch">
                                      <HStack justify="space-between">
                                        <Text fontWeight="bold" color="red.800">
                                          {request.customer.first_name} {request.customer.last_name} - {request.action.multaData?.mes}
                                        </Text>
                                        {request.boleta && (
                                          <Badge colorScheme="red" variant="solid">
                                            Boleta: {request.boleta}
                                          </Badge>
                                        )}
                                      </HStack>
                                      <Text fontSize="sm" color="red.700">
                                        {request.description}
                                      </Text>
                                      <Box bg="white" p={3} borderRadius="md" border="1px solid" borderColor="red.300">
                                        <Text fontSize="xs" fontWeight="bold" mb={2} color="gray.700">
                                          {request.method} {request.endpoint}
                                        </Text>
                                        <Box bg="gray.50" p={2} borderRadius="md" fontFamily="mono" fontSize="xs">
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(request.payload, null, 2)}
                                          </pre>
                                        </Box>
                                      </Box>
                                    </VStack>
                                  </Box>
                                ))}
                              </VStack>
                            </Box>
                          ) : (
                            <Text color="gray.500" textAlign="center" py={8}>
                              No hay multas para crear
                            </Text>
                          )}
                        </TabPanel>

                        {/* Tab: Resumen general */}
                        <TabPanel p={0} pt={4}>
                          <Box bg="gray.50" p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
                            <Text fontSize="sm" fontWeight="bold" mb={2} color="gray.700">
                              📋 Resumen de {requestPreview.length} peticiones HTTP:
                            </Text>
                            <VStack spacing={2} align="stretch">
                              {selectedSyncActions.map((comparison, index) => (
                                <HStack key={index} justify="space-between" fontSize="sm">
                                  <Text fontWeight="medium">
                                    {comparison.customer.first_name} {comparison.customer.last_name}
                                  </Text>
                                  <Text color="gray.600">
                                    {comparison.syncActions.length} petición{comparison.syncActions.length !== 1 ? 'es' : ''}
                                  </Text>
                                </HStack>
                              ))}
                            </VStack>
                          </Box>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                    
                    <Divider />

                    {/* Barra de progreso en tiempo real */}
                    {isExecutingSync && (
                      <Box bg="blue.50" p={4} borderRadius="md" border="1px solid" borderColor="blue.200">
                        <VStack spacing={3} align="stretch">
                          <HStack justify="space-between" align="center">
                            <Text fontSize="sm" fontWeight="bold" color="blue.800">
                              {syncProgress.phase === 'retry' ? '🔄 Procesando reintentos...' : '📤 Ejecutando peticiones...'}
                            </Text>
                            <Text fontSize="sm" color="blue.600">
                              {syncProgress.processed} / {syncProgress.total} ({syncProgress.percentage}%)
                            </Text>
                          </HStack>
                          
                          <Progress 
                            value={syncProgress.percentage} 
                            colorScheme={syncProgress.phase === 'retry' ? 'orange' : 'blue'}
                            size="lg" 
                            hasStripe 
                            isAnimated 
                          />
                          
                          <SimpleGrid columns={4} spacing={2}>
                            <Text fontSize="xs" textAlign="center">
                              <strong>✅ Actualizadas:</strong><br/>
                              {syncProgress.current.ordersUpdated}
                            </Text>
                            <Text fontSize="xs" textAlign="center">
                              <strong>➕ Creadas:</strong><br/>
                              {syncProgress.current.ordersCreated}
                            </Text>
                            <Text fontSize="xs" textAlign="center">
                              <strong>❌ Errores:</strong><br/>
                              {syncProgress.current.errors}
                            </Text>
                            <Text fontSize="xs" textAlign="center">
                              <strong>🔄 Recuperadas:</strong><br/>
                              {syncProgress.current.retried}
                            </Text>
                          </SimpleGrid>
                        </VStack>
                      </Box>
                    )}

                    <HStack spacing={3} justify="center">
                      <Button
                        variant="outline"
                        onClick={() => setNapsisStep(2)}
                        isDisabled={isExecutingSync}
                      >
                        ← Volver a Revisar
                      </Button>
                      <Button
                        colorScheme="red"
                        size="lg"
                        onClick={executeNapsisSync}
                        isLoading={isExecutingSync}
                        loadingText="Ejecutando peticiones..."
                        leftIcon={<Icon as={FiRefreshCw} />}
                      >
                        🚀 EJECUTAR {requestPreview.length} PETICIONES →
                      </Button>
                    </HStack>
                  </VStack>
                );
              })()}

              {napsisStep === 4 && syncResult && (
                <SyncResults
                  syncResult={syncResult}
                  comparisonResult={comparisonResult}
                  onExportReport={() => {
                    toast({
                      title: "Reporte exportado",
                      description: "El reporte ha sido descargado",
                      status: "success",
                      duration: 3000,
                      isClosable: true,
                    });
                  }}
                />
              )}
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNapsisModalOpen(false);
                if (napsisStep === 4 && syncResult) {
                  // Recargar datos de pagos para ver cambios
                  loadPaymentsData();
                }
              }}
              isDisabled={isExecutingSync}
            >
              {napsisStep === 4 ? "Cerrar y Actualizar" : "Cancelar"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default Billing;
